import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { useTypedSelector } from 'src/State/RootReducer'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack, Typography, useMediaQuery, useTheme, CircularProgress } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useCountryOptions } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { PositionedAction } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { InstallationAddressFormValues, InstallationAddressFormFields } from 'src/Libs/installation-details/types'
import { useValidateInstallationAddressForm } from 'src/Libs/installation-details/hooks'
import { InformationBox } from 'src/Components'
import { isFlowTheme } from 'src/Libs/utils'

const useStyles = makeStyles()(theme => ({
  title: {
    padding: theme.spacing(2),
  },
}))

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: InstallationAddressFormValues) => void
  isPending: boolean
  errorCode?: number | false
}

/**
 * Dialog to get address of installation from owner.
 */
const InstallationAddressFormDialog: React.FC<Props> = ({ isOpen, onClose, onSubmit, isPending, errorCode }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { classes } = useStyles()
  const { supportedE3dcInstallationCountries, supportedFlowInstallationCountries } = useCountryOptions()
  const { installationAddressFormSchema } = useValidateInstallationAddressForm()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const installation = useTypedSelector(state => state.storage.installation)
  const supportedCountries = [
    ...(isFlowTheme ? supportedFlowInstallationCountries : supportedE3dcInstallationCountries),
    { label: t('installation-details.installation-address.country.others'), value: 'others' },
  ]

  const methods = useForm<InstallationAddressFormValues>({
    resolver: zodResolver(installationAddressFormSchema),
    defaultValues: {
      streetName: installation?.streetName ?? '',
      streetNumber: installation?.streetNumber ?? '',
      zip: installation?.zip ?? '',
      city: installation?.city ?? '',
      countryCode:
        installation?.countryCode && supportedCountries.some(country => country.value === installation.countryCode)
          ? installation.countryCode
          : 'others',
    },
    mode: 'onChange',
  })

  const handleSubmit: SubmitHandler<InstallationAddressFormValues> = async (data): Promise<void> => {
    onSubmit(data)
  }

  const isCountryOthers = methods.watch(InstallationAddressFormFields.countryCode) === 'others'

  const secondaryAction: PositionedAction = {
    position: '3',
    style: 'secondary',
    name: t('installation-details.installation-address.remind-me-later'),
    onClick: onClose,
  }

  const primaryAction: PositionedAction = {
    position: '4',
    style: 'primary',
    name: t('common.save'),
    onClick: methods.handleSubmit(handleSubmit),
    /* form stays disabled when attempting to save address or country not in list */
    disabled: !methods.formState.isValid || isPending || isCountryOthers,
  }

  return (
    <Dialog
      open={isOpen}
      fullScreen={!upMd}
      navigateBackButton={false}
      closeButtonVisible={false}
      onClose={onClose}
      onNavigateBack={onClose}
      title={t('installation-details.installation-address.title')}
      dialogTitleClassName={classes.title}
      actions={[primaryAction, secondaryAction]}
      maxWidth="sm"
    >
      <FormProvider {...methods}>
        <Box sx={{ position: 'relative' }}>
          {isPending && (
            <Stack justifyContent="center" alignItems="center" sx={{ position: 'fixed', inset: 0, zIndex: 1 }}>
              <CircularProgress />
            </Stack>
          )}
          <Stack spacing={2} sx={{ opacity: isPending ? 0 : 1, pointerEvents: isPending ? 'none' : 'auto' }}>
            <Typography variant="body2" gutterBottom>
              {t('installation-details.installation-address.description')}
            </Typography>

            <InformationBox messageType="warning" text={t('installation-details.installation-address.warning')} />

            <TextField name={InstallationAddressFormFields.streetName} label={t('common.street')} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField name={InstallationAddressFormFields.streetNumber} label={t('common.streetNumber')} fullWidth />
              <TextField name={InstallationAddressFormFields.zip} label={t('common.zipCode')} fullWidth />
            </Stack>
            <TextField name={InstallationAddressFormFields.city} label={t('common.location')} fullWidth />
            <Select name={InstallationAddressFormFields.countryCode} label={t('common.country')} options={supportedCountries} fullWidth />
            {isCountryOthers && <InformationBox messageType="info" text={t('installation-details.installation-address.info.country-others')} />}

            {!isCountryOthers && errorCode === FetchErrorCodes.InvalidAddressZip && (
              <InformationBox messageType="error" text={t('installation-details.installation-address.error.invalid-zip')} />
            )}
            {!isCountryOthers && errorCode === FetchErrorCodes.InvalidAddressCity && (
              <InformationBox messageType="error" text={t('installation-details.installation-address.error.invalid-city')} />
            )}
          </Stack>
        </Box>
      </FormProvider>
    </Dialog>
  )
}

export default InstallationAddressFormDialog
